import React, { useState, useRef, useEffect } from 'react';
import { Edit, Save, X, Play, Loader } from 'lucide-react';
import { analyzePreUploadScorm, editPreUploadScorm, validatePreUploadScorm } from '../services/api';
import { toast } from 'react-toastify';

interface ScormSlide {
  id: string;
  name: string;
  duration: string;
  durationInFrames: number;
  durationInSeconds: number;
}

interface ScormAnalysis {
  slides: ScormSlide[];
  videos: Array<{
    fileName: string;
    duration: string;
    durationInSeconds: number;
  }>;
  referencedVideo?: string;
}

interface PreUploadScormEditorProps {
  file: File;
  onSave: (editedFile: File) => void;
  onCancel: () => void;
}

const PreUploadScormEditor: React.FC<PreUploadScormEditorProps> = ({ 
  file, 
  onSave, 
  onCancel 
}) => {
  const [isAnalyzing, setIsAnalyzing] = useState(true);
  const [analysis, setAnalysis] = useState<ScormAnalysis | null>(null);
  const [selectedSlide, setSelectedSlide] = useState<ScormSlide | null>(null);
  const [newSlideName, setNewSlideName] = useState('');
  const [newDuration, setNewDuration] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editedFile, setEditedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  // Create a temporary URL for the file
  useEffect(() => {
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [file]);

  // Helper function to convert frames to time string
  const framesToTimeString = (frames: number): string => {
    const seconds = Math.floor(frames / 30); // Assuming 30fps
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Helper function to escape regex characters
  const escapeRegex = (str: string) => {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  };

  // Helper function to update project.js completely with all slide properties
  const updateProjectJsCompletely = (
    jsContent: string, 
    slideId: string, 
    oldName: string, 
    newName: string
  ): string => {
    let updated = jsContent;
    
    // Store original for comparison
    const originalContent = jsContent;
    
    // 1. Update lb property (label) - the display name
    const lbPattern = new RegExp(`(${slideId}:\\{[^}]*lb:['"])${escapeRegex(oldName)}(['"])`, 'g');
    updated = updated.replace(lbPattern, `$1${newName}$2`);
    
    // 2. Update sk property (slide key) - used for navigation
    const skPattern = new RegExp(`(sk:['"])${escapeRegex(oldName)}(['"])`, 'gi');
    updated = updated.replace(skPattern, `$1${newName}$2`);
    
    // 3. Update slideTag property - used for CSS/identification
    const oldTag = oldName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    const newTag = newName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    const slideTagPattern = new RegExp(`(slideTag:['"])${escapeRegex(oldTag)}(-slide['"])`, 'gi');
    updated = updated.replace(slideTagPattern, `$1${newTag}$2`);
    
    // 4. Update option properties - used in various places
    const oldOption = oldName.toUpperCase().replace(/\s+/g, '_').replace(/[^A-Z0-9_]/g, '');
    const newOption = newName.toUpperCase().replace(/\s+/g, '_').replace(/[^A-Z0-9_]/g, '');
    
    if (oldOption && newOption) {
      const optionPattern = new RegExp(`(option:['"])([^'"]*${escapeRegex(oldOption)}[^'"]*)(['"])`, 'gi');
      updated = updated.replace(optionPattern, (match, p1, p2, p3) => {
        const newP2 = p2.replace(new RegExp(escapeRegex(oldOption), 'g'), newOption);
        return p1 + newP2 + p3;
      });
    }
    
    // 5. Log what was updated
    const changeCount = (updated.match(new RegExp(escapeRegex(newName), 'g')) || []).length - 
                       (originalContent.match(new RegExp(escapeRegex(newName), 'g')) || []).length;
    
    console.log(`[PreUpload] Updated slide name in project.js:`);
    console.log(`   Old name: "${oldName}"`);
    console.log(`   New name: "${newName}"`);
    console.log(`   Properties updated: lb, sk, slideTag, option`);
    console.log(`   Total replacements: ${changeCount}`);
    
    return updated;
  };

  // Helper function to update project.js frame ranges for duration changes
  const updateProjectJsFrameRanges = (
    jsContent: string,
    slideId: string,
    newDuration: number,
    oldDuration: number
  ): string => {
    let updated = jsContent;
    const durationDifference = newDuration - oldDuration;
    
    if (durationDifference === 0) {
      return updated;
    }
    
    try {
      // Update the frame range in the slide object
      // Pattern: SlideXXXX:{...from:181,to:35398...}
      const slideObjPattern = new RegExp(
        `(${slideId}:\\{[^}]*from:)(\\d+)(,to:)(\\d+)`,
        'g'
      );
      
      updated = updated.replace(
        slideObjPattern,
        (match, p1, oldFrom, p3, oldTo) => {
          const oldDuration = parseInt(oldTo) - parseInt(oldFrom);
          const newTo = parseInt(oldFrom) + newDuration;
          
          console.log(`📐 Updating frame range: from ${oldFrom} to ${oldTo} → from ${oldFrom} to ${newTo}`);
          console.log(`   Old duration: ${oldDuration} frames`);
          console.log(`   New duration: ${newDuration} frames`);
          
          return p1 + oldFrom + p3 + newTo;
        }
      );
      
      // Also update any duration properties in the slide object
      const durationPattern = new RegExp(
        `(${slideId}:\\{[^}]*duration:)(\\d+)`,
        'g'
      );
      
      updated = updated.replace(
        durationPattern,
        `$1${newDuration}`
      );
      
      // Cascade update for subsequent slides
      updated = updateSubsequentSlideRanges(updated, slideId, durationDifference);
      
      // Update global duration in project.js
      const globalDurationPattern = /(durationInFrames:)(\d+)/g;
      updated = updated.replace(
        globalDurationPattern,
        (match, p1, oldDuration) => {
          const newGlobalDuration = parseInt(oldDuration) + durationDifference;
          console.log(`🌍 Updated global durationInFrames in project.js: ${oldDuration} → ${newGlobalDuration}`);
          return p1 + newGlobalDuration;
        }
      );
      
    } catch (error) {
      console.error('[PreUpload] Error updating frame ranges in project.js:', error);
    }
    
    return updated;
  };

  // Helper function to update subsequent slide frame ranges when duration changes
  const updateSubsequentSlideRanges = (
    jsContent: string,
    currentSlideId: string,
    durationDifference: number
  ): string => {
    let updated = jsContent;
    
    // Find all slide objects and their current ranges
    const slidePattern = /Slide(\d+):\{[^}]*from:(\d+),to:(\d+)/g;
    const slides: Array<{id: string, from: number, to: number}> = [];
    let match;
    
    while ((match = slidePattern.exec(jsContent)) !== null) {
      slides.push({
        id: `Slide${match[1]}`,
        from: parseInt(match[2]),
        to: parseInt(match[3])
      });
    }
    
    // Sort slides by 'from' value
    slides.sort((a, b) => a.from - b.from);
    
    // Find the index of the current slide
    const currentIndex = slides.findIndex(s => s.id === currentSlideId);
    
    if (currentIndex !== -1) {
      // Update all slides after the current one
      for (let i = currentIndex + 1; i < slides.length; i++) {
        const slide = slides[i];
        const newFrom = slide.from + durationDifference;
        const newTo = slide.to + durationDifference;
        
        const slideUpdatePattern = new RegExp(
          `(${slide.id}:\\{[^}]*from:)${slide.from}(,to:)${slide.to}`,
          'g'
        );
        
        updated = updated.replace(
          slideUpdatePattern,
          `$1${newFrom}$2${newTo}`
        );
        
        console.log(`🔄 Updated ${slide.id} range: ${slide.from}-${slide.to} → ${newFrom}-${newTo}`);
      }
    }
    
    return updated;
  };

  // Validation function to check SCORM duration consistency
  const validateScormDurations = (projectConfig: any) => {
    const issues: Array<{type: string, message: string, slides?: any[]}> = [];
    
    // Check if global duration matches sum of slides
    if (projectConfig.metadata?.durationInFrames !== undefined) {
      let calculatedTotal = 0;
      const slideDurations: Array<{id: string, name: string, duration: number}> = [];
      
      if (projectConfig.contentStructure) {
        for (const item of projectConfig.contentStructure) {
          if (item.roles?.slide?.durationInFrames) {
            calculatedTotal += item.roles.slide.durationInFrames;
            slideDurations.push({
              id: item.id,
              name: item.instance || item.name,
              duration: item.roles.slide.durationInFrames
            });
          }
        }
      }
      
      if (Math.abs(projectConfig.metadata.durationInFrames - calculatedTotal) > 1) {
        issues.push({
          type: 'duration_mismatch',
          message: `Global duration (${projectConfig.metadata.durationInFrames}) doesn't match sum of slides (${calculatedTotal})`,
          slides: slideDurations
        });
      }
    }
    
    return issues;
  };

  // Analyze the SCORM file directly in the browser
  useEffect(() => {
    const analyzeFile = async () => {
      try {
        setIsAnalyzing(true);
        setError(null);

        // Analyze the SCORM file directly in the browser using JSZip
        const JSZip = require('jszip');
        const zip = new JSZip();
        const zipContent = await zip.loadAsync(file);
        
        // Look for project files
        const projectTxt = zipContent.file('project.txt');
        const projectJs = zipContent.file('project.js');
        
        let projectConfig = null;
        
        if (projectTxt) {
          const content = await projectTxt.async('string');
          try {
            projectConfig = JSON.parse(content);
          } catch (e) {
            console.warn('Failed to parse project.txt as JSON');
          }
        }
        
        if (!projectConfig && projectJs) {
          const content = await projectJs.async('string');
          // Try to extract JSON from JavaScript file
          const jsonMatch = content.match(/\{[\s\S]*\}/);
          if (jsonMatch) {
            try {
              projectConfig = JSON.parse(jsonMatch[0]);
            } catch (e) {
              console.warn('Failed to parse project.js JSON');
            }
          }
        }

        // Extract slides and timing information
        const slides: ScormSlide[] = [];
        const videos: Array<{fileName: string, duration: string, durationInSeconds: number}> = [];

        if (projectConfig?.contentStructure) {
          for (const item of projectConfig.contentStructure) {
            if (item.roles && item.roles.slide) {
              const slide: ScormSlide = {
                id: item.id,
                name: item.instance || item.name || 'Unnamed Slide',
                duration: framesToTimeString(item.roles.slide.durationInFrames || 0),
                durationInFrames: item.roles.slide.durationInFrames || 0,
                durationInSeconds: (item.roles.slide.durationInFrames || 0) / 30
              };
              slides.push(slide);
            }
          }
        }

        // Extract video information
        if (projectConfig?.eventVideos) {
          for (const video of projectConfig.eventVideos) {
            const videoInfo = {
              fileName: video.uri ? video.uri.split('/').pop() || '' : '',
              duration: framesToTimeString(video.durationInFrames || 0),
              durationInSeconds: (video.durationInFrames || 0) / 30
            };
            videos.push(videoInfo);
          }
        }

        const analysis: ScormAnalysis = {
          slides,
          videos,
          referencedVideo: projectConfig?.eventVideos?.[0]?.uri ? projectConfig.eventVideos[0].uri.split('/').pop() || '' : undefined
        };

        setAnalysis(analysis);
        
        // Auto-select the first slide
        if (analysis.slides.length > 0) {
          setSelectedSlide(analysis.slides[0]);
          setNewSlideName(analysis.slides[0].name);
          setNewDuration(analysis.slides[0].duration);
        }

      } catch (error) {
        console.error('Error analyzing SCORM file:', error);
        setError('Failed to analyze SCORM file. Please ensure it\'s a valid SCORM package.');
      } finally {
        setIsAnalyzing(false);
      }
    };

    analyzeFile();
  }, [file]);

  const handleSlideSelect = (slide: ScormSlide) => {
    setSelectedSlide(slide);
    setNewSlideName(slide.name);
    setNewDuration(slide.duration);
  };

  const handleSaveChanges = async () => {
    if (!selectedSlide || !analysis) return;

    try {
      setIsSaving(true);
      setError(null);

      // Edit the SCORM file directly in the browser
      const JSZip = require('jszip');
      const zip = new JSZip();
      const zipContent = await zip.loadAsync(file);
      
      // Look for project files
      const projectTxt = zipContent.file('project.txt');
      const projectJs = zipContent.file('project.js');
      
      let projectConfig = null;
      let isProjectJs = false;
      
      if (projectTxt) {
        const content = await projectTxt.async('string');
        try {
          projectConfig = JSON.parse(content);
        } catch (e) {
          console.warn('Failed to parse project.txt as JSON');
        }
      }
      
      if (!projectConfig && projectJs) {
        const content = await projectJs.async('string');
        
        // Try multiple patterns to extract JSON from project.js
        let jsonMatch = null;
        
        // Pattern 1: Variable assignment
        jsonMatch = content.match(/(?:var\s+\w+\s*=\s*|window\.\w+\s*=\s*)(\{[\s\S]*\});?\s*$/);
        
        // Pattern 2: Direct object
        if (!jsonMatch) {
          jsonMatch = content.match(/(\{[\s\S]*\})\s*;?\s*$/);
        }
        
        if (jsonMatch) {
          try {
            projectConfig = JSON.parse(jsonMatch[1]);
            isProjectJs = true;
            console.log('[PreUpload] Successfully parsed project.js JSON');
          } catch (e) {
            console.error('Failed to parse project.js JSON:', e);
          }
        }
      }

      if (!projectConfig) {
        throw new Error('Could not find or parse project configuration files');
      }

      // Apply edits to the project configuration
      let oldSlideName = '';
      let durationInFrames = 0;
      let oldDurationInFrames = 0;
      
      if (projectConfig.contentStructure) {
        const slide = projectConfig.contentStructure.find((s: any) => s.id === selectedSlide.id);
        if (slide && slide.roles && slide.roles.slide) {
          console.log('[PreUpload] Found slide to edit:', slide.id);
          console.log('[PreUpload] Original name:', slide.instance || slide.name);
          console.log('[PreUpload] Original duration:', slide.roles.slide.durationInFrames);
          
          // Store the old name for reference
          oldSlideName = slide.instance || slide.name || '';
          
          // Update slide name
          if (newSlideName && newSlideName.trim() !== '') {
            slide.instance = newSlideName.trim();
            if (slide.name) {
              slide.name = newSlideName.trim();
            }
            console.log('[PreUpload] Updated name to:', newSlideName.trim());
          }

          // Update slide duration
          oldDurationInFrames = slide.roles.slide.durationInFrames || 0;
          
          if (newDuration && newDuration.trim() !== '') {
            durationInFrames = timeStringToFrames(newDuration);
            const durationDifference = durationInFrames - oldDurationInFrames;
            
            slide.roles.slide.durationInFrames = durationInFrames;
            slide.roles.slide.duration = durationInFrames;
            slide.roles.slide.durationInSeconds = durationInFrames / 30;
            
            // Update global duration with proper validation
            if (projectConfig.metadata?.durationInFrames !== undefined && durationDifference !== 0) {
              const oldGlobalDuration = projectConfig.metadata.durationInFrames;
              
              // Calculate the actual total duration by summing all slides
              let calculatedTotalDuration = 0;
              if (projectConfig.contentStructure) {
                for (const item of projectConfig.contentStructure) {
                  if (item.roles?.slide?.durationInFrames) {
                    if (item.id === selectedSlide.id) {
                      // Use new duration for the edited slide
                      calculatedTotalDuration += durationInFrames;
                    } else {
                      // Use existing duration for other slides
                      calculatedTotalDuration += item.roles.slide.durationInFrames;
                    }
                  }
                }
              }
              
              // Use the calculated total instead of simple addition
              projectConfig.metadata.durationInFrames = calculatedTotalDuration;
              
              console.log(`🌍 Updated global duration: ${oldGlobalDuration} → ${projectConfig.metadata.durationInFrames}`);
              console.log(`   (Calculated from sum of all slides: ${calculatedTotalDuration} frames)`);
              
              // Validate the result
              if (projectConfig.metadata.durationInFrames < 0) {
                console.error('❌ ERROR: Global duration would be negative! Using calculated total instead.');
                projectConfig.metadata.durationInFrames = calculatedTotalDuration;
              }
            }
            
            console.log('[PreUpload] Updated duration to:', durationInFrames, 'frames');
            console.log('[PreUpload] Duration difference:', durationDifference, 'frames');
          }

          // Also update navigation and table of contents if they exist - COMPREHENSIVE UPDATE like post-upload editor
          if (projectConfig.navigation) {
            const updateNavigationTiming = (navItems: any[]) => {
              if (!Array.isArray(navItems)) return;
              
              for (const navItem of navItems) {
                if (navItem.id === selectedSlide.id) {
                  if (navItem.duration !== undefined) {
                    navItem.duration = durationInFrames;
                    console.log(`🔄 Updated navigation item duration: ${durationInFrames}`);
                  }
                  if (navItem.durationInFrames !== undefined) {
                    navItem.durationInFrames = durationInFrames;
                    console.log(`🔄 Updated navigation item durationInFrames: ${durationInFrames}`);
                  }
                  if (navItem.durationInSeconds !== undefined) {
                    navItem.durationInSeconds = durationInFrames / 30;
                    console.log(`🔄 Updated navigation item durationInSeconds: ${durationInFrames / 30}`);
                  }
                  if (newSlideName && newSlideName.trim() !== '' && navItem.name !== undefined) {
                    navItem.name = newSlideName.trim();
                    console.log(`🔄 Updated navigation item name: "${newSlideName.trim()}"`);
                  }
                  if (newSlideName && newSlideName.trim() !== '' && navItem.title !== undefined) {
                    navItem.title = newSlideName.trim();
                    console.log(`🔄 Updated navigation item title: "${newSlideName.trim()}"`);
                  }
                }
                
                // Recursively check nested navigation items
                if (navItem.children) {
                  updateNavigationTiming(navItem.children);
                }
              }
            };
            
            updateNavigationTiming(projectConfig.navigation);
          }

          if (projectConfig.tableOfContents) {
            const updateTocTiming = (tocItems: any[]) => {
              if (!Array.isArray(tocItems)) return;
              
              for (const tocItem of tocItems) {
                if (tocItem.id === selectedSlide.id) {
                  if (tocItem.duration !== undefined) {
                    tocItem.duration = durationInFrames;
                    console.log(`🔄 Updated TOC item duration: ${durationInFrames}`);
                  }
                  if (tocItem.durationInFrames !== undefined) {
                    tocItem.durationInFrames = durationInFrames;
                    console.log(`🔄 Updated TOC item durationInFrames: ${durationInFrames}`);
                  }
                  if (tocItem.durationInSeconds !== undefined) {
                    tocItem.durationInSeconds = durationInFrames / 30;
                    console.log(`🔄 Updated TOC item durationInSeconds: ${durationInFrames / 30}`);
                  }
                  if (newSlideName && newSlideName.trim() !== '' && tocItem.name !== undefined) {
                    tocItem.name = newSlideName.trim();
                    console.log(`🔄 Updated TOC item name: "${newSlideName.trim()}"`);
                  }
                  if (newSlideName && newSlideName.trim() !== '' && tocItem.title !== undefined) {
                    tocItem.title = newSlideName.trim();
                    console.log(`🔄 Updated TOC item title: "${newSlideName.trim()}"`);
                  }
                }
                
                // Recursively check nested TOC items
                if (tocItem.children) {
                  updateTocTiming(tocItem.children);
                }
              }
            };
            
            updateTocTiming(projectConfig.tableOfContents);
          }
        } else {
          console.error('[PreUpload] Could not find slide with ID:', selectedSlide.id);
        }
      }

      // Update the project file in the ZIP - use same format as post-upload editor
      const updatedProjectContent = JSON.stringify(projectConfig, null, 0); // No indentation like post-upload editor
      
      if (isProjectJs) {
        // Enhanced project.js update with comprehensive handling
        const originalContent = await projectJs.async('string');
        let updatedJsContent = originalContent;
        
        // Apply the same edits as project.txt to the JSON portion
        if (projectConfig.contentStructure) {
          const slide = projectConfig.contentStructure.find((s: any) => s.id === selectedSlide.id);
          if (slide) {
            // Update slide name
            const oldSlideName = slide.instance || slide.name || '';
            if (newSlideName && newSlideName.trim() !== '') {
              slide.instance = newSlideName.trim();
              if (slide.name) {
                slide.name = newSlideName.trim();
              }
            }
            
            // Update duration with proper validation
            if (newDuration && newDuration.trim() !== '') {
              const durationInFrames = timeStringToFrames(newDuration);
              slide.roles.slide.durationInFrames = durationInFrames;
              slide.roles.slide.duration = durationInFrames;
              slide.roles.slide.durationInSeconds = durationInFrames / 30;
            }
            
            // Update project.js with ALL name references
            if (newSlideName && newSlideName.trim() !== '' && oldSlideName) {
              console.log('[PreUpload] Using comprehensive slide name update approach for main project.js');
              updatedJsContent = updateProjectJsCompletely(updatedJsContent, selectedSlide.id, oldSlideName, newSlideName.trim());
            }
            
            // Update project.js with frame range updates
            if (newDuration && newDuration.trim() !== '' && oldDurationInFrames !== durationInFrames) {
              console.log('[PreUpload] Using comprehensive duration update approach for main project.js');
              updatedJsContent = updateProjectJsFrameRanges(updatedJsContent, selectedSlide.id, durationInFrames, oldDurationInFrames);
            }
            
            // Also update the JSON portion with the new config
            const updatedJson = JSON.stringify(projectConfig, null, 0);
            
            // Try to replace the JSON object in the original file
            let finalJsContent = null;
            
            // Pattern 1: Variable assignment
            const match1 = originalContent.match(/(?:var\s+\w+\s*=\s*|window\.\w+\s*=\s*)(\{[\s\S]*\});?\s*$/);
            if (match1) {
              finalJsContent = originalContent.replace(match1[1], updatedJson);
            }
            
            // Pattern 2: Direct object
            if (!finalJsContent) {
              const match2 = originalContent.match(/(\{[\s\S]*\})\s*;?\s*$/);
              if (match2) {
                finalJsContent = originalContent.replace(match2[1], updatedJson);
              }
            }
            
            if (finalJsContent) {
              // Apply the comprehensive updates to the final content
              if (newSlideName && newSlideName.trim() !== '' && oldSlideName) {
                finalJsContent = updateProjectJsCompletely(finalJsContent, selectedSlide.id, oldSlideName, newSlideName.trim());
              }
              if (newDuration && newDuration.trim() !== '' && oldDurationInFrames !== durationInFrames) {
                finalJsContent = updateProjectJsFrameRanges(finalJsContent, selectedSlide.id, durationInFrames, oldDurationInFrames);
              }
              
              zipContent.file('project.js', finalJsContent);
              console.log('[PreUpload] Updated project.js with all slide references');
            } else {
              // Fallback: just replace the entire content
              zipContent.file('project.js', `var projectConfig = ${updatedJson};`);
            }
          }
        }
      } else {
        // Update project.txt
        zipContent.file('project.txt', updatedProjectContent);
      }

      // Also update the companion file - same logic as post-upload editor
      if (isProjectJs) {
        // We updated project.js, now also update project.txt
        zipContent.file('project.txt', updatedProjectContent);
      } else if (projectJs) {
        // We updated project.txt, now also update project.js with comprehensive updates
        const originalContent = await projectJs.async('string');
        let updatedJsContent = originalContent;
        
        // Apply comprehensive updates to companion project.js
        if (newSlideName && newSlideName.trim() !== '' && oldSlideName) {
          console.log('[PreUpload] Using comprehensive slide name update approach for companion project.js');
          updatedJsContent = updateProjectJsCompletely(updatedJsContent, selectedSlide.id, oldSlideName, newSlideName.trim());
        }
        
        if (newDuration && newDuration.trim() !== '' && oldDurationInFrames !== durationInFrames) {
          console.log('[PreUpload] Using comprehensive duration update approach for companion project.js');
          updatedJsContent = updateProjectJsFrameRanges(updatedJsContent, selectedSlide.id, durationInFrames, oldDurationInFrames);
        }
        
        // Also update the JSON portion with the new config
        const updatedJson = JSON.stringify(projectConfig, null, 0);
        
        // Try to replace the JSON object in the companion file
        let finalJsContent = null;
        
        // Pattern 1: Variable assignment
        const match1 = originalContent.match(/(?:var\s+\w+\s*=\s*|window\.\w+\s*=\s*)(\{[\s\S]*\});?\s*$/);
        if (match1) {
          finalJsContent = originalContent.replace(match1[1], updatedJson);
        }
        
        // Pattern 2: Direct object
        if (!finalJsContent) {
          const match2 = originalContent.match(/(\{[\s\S]*\})\s*;?\s*$/);
          if (match2) {
            finalJsContent = originalContent.replace(match2[1], updatedJson);
          }
        }
        
        if (finalJsContent) {
          // Apply the comprehensive updates to the final content
          if (newSlideName && newSlideName.trim() !== '' && oldSlideName) {
            finalJsContent = updateProjectJsCompletely(finalJsContent, selectedSlide.id, oldSlideName, newSlideName.trim());
          }
          if (newDuration && newDuration.trim() !== '' && oldDurationInFrames !== durationInFrames) {
            finalJsContent = updateProjectJsFrameRanges(finalJsContent, selectedSlide.id, durationInFrames, oldDurationInFrames);
          }
          
          zipContent.file('project.js', finalJsContent);
          console.log('[PreUpload] Updated companion project.js with all slide references');
        } else {
          // Fallback: just replace the entire content
          zipContent.file('project.js', `var projectConfig = ${updatedJson};`);
        }
      }

      // Generate the edited ZIP file
      const editedZipBlob = await zipContent.generateAsync({ type: 'blob' });
      const newFile = new File([editedZipBlob], file.name, { type: 'application/zip' });
      
      console.log('[PreUpload] Generated edited file:', newFile.name, 'Size:', newFile.size);
      console.log('[PreUpload] Original file size:', file.size);
      
      setEditedFile(newFile);
      toast.success('SCORM content edited successfully!');

    } catch (error) {
      console.error('Error saving changes:', error);
      setError('Failed to save changes. Please try again.');
      toast.error('Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  };

  const timeStringToFrames = (timeString: string): number => {
    const parts = timeString.split(':').map(Number);
    const hours = parts[0] || 0;
    const minutes = parts[1] || 0;
    const seconds = parts[2] || 0;
    const totalSeconds = hours * 3600 + minutes * 60 + seconds;
    return Math.round(totalSeconds * 30); // Assuming 30fps
  };

  const handleFinalSave = () => {
    if (editedFile) {
      onSave(editedFile);
    } else {
      onSave(file); // Save original file if no edits were made
    }
  };

  const formatTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  if (isAnalyzing) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
          <div className="flex items-center justify-center mb-4">
            <Loader className="animate-spin h-8 w-8 text-blue-500 mr-3" />
            <h2 className="text-xl font-semibold">Analyzing SCORM Package</h2>
          </div>
          <p className="text-gray-600 text-center">
            Extracting slide information and timing data...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-red-600 mb-4">Analysis Failed</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <div className="flex justify-center space-x-4">
              <button
                onClick={onCancel}
                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-semibold">Edit SCORM Content</h2>
          <button
            onClick={onCancel}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Panel - Slide List */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Slides</h3>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {analysis?.slides.map((slide) => (
                <div
                  key={slide.id}
                  onClick={() => handleSlideSelect(slide)}
                  className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                    selectedSlide?.id === slide.id
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="font-medium">{slide.name}</div>
                  <div className="text-sm text-gray-600">
                    Duration: {slide.duration}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Panel - Edit Form */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Edit Slide</h3>
            
            {selectedSlide && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Slide Name
                  </label>
                  <input
                    type="text"
                    value={newSlideName}
                    onChange={(e) => setNewSlideName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900"
                    placeholder="Enter slide name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Duration (HH:MM:SS)
                  </label>
                  <input
                    type="text"
                    value={newDuration}
                    onChange={(e) => setNewDuration(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900"
                    placeholder="00:00:00"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Format: HH:MM:SS (e.g., 00:02:30 for 2 minutes 30 seconds)
                  </p>
                </div>

                <div className="flex space-x-3">
                  <button
                    onClick={handleSaveChanges}
                    disabled={isSaving}
                    className="flex items-center px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
                  >
                    {isSaving ? (
                      <Loader className="animate-spin h-4 w-4 mr-2" />
                    ) : (
                      <Save className="h-4 w-4 mr-2" />
                    )}
                    {isSaving ? 'Saving...' : 'Save Changes'}
                  </button>
                  
                  <button
                    onClick={() => {
                      setNewSlideName(selectedSlide.name);
                      setNewDuration(selectedSlide.duration);
                    }}
                    className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                  >
                    Reset
                  </button>
                </div>

                {editedFile && (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center text-green-800">
                      <Save className="h-4 w-4 mr-2" />
                      Changes saved successfully!
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Preview Section */}
        {previewUrl && (
          <div className="mt-6">
            <h3 className="text-lg font-semibold mb-3">Preview</h3>
            <div className="border border-gray-200 rounded-lg p-4">
              <p className="text-sm text-gray-600 mb-2">
                File: {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
              </p>
              <div className="bg-gray-100 rounded p-4 text-center">
                <Play className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                <p className="text-sm text-gray-600">
                  SCORM package preview (content will be available after upload)
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end space-x-4 mt-6 pt-6 border-t">
          <button
            onClick={onCancel}
            className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
          >
            Cancel
          </button>
          <button
            onClick={handleFinalSave}
            className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
          >
            Save & Continue
          </button>
        </div>
      </div>
    </div>
  );
};

export default PreUploadScormEditor; 